import { DesignSystemUi } from "@blockdaemon-internal/design-system";
import { Center, Loader } from "@mantine/core";
import { QueryClientProvider } from "@tanstack/react-query";
import { createRouter, RouterProvider } from "@tanstack/react-router";
import { StrictMode, Suspense } from "react";
import { createRoot } from "react-dom/client";
import packageJson from "../package.json";
import { WidgetConfigProvider } from "./features/customization/hooks/use-widget-config";
import type { DefiTabRouteId } from "./features/defi/hooks/use-defi-tabs";
import { WalletProvider } from "./features/wallet/components/wallet-provider";
import { initializeI18n } from "./lib/i18n/init";
import { LocaleConfigWrapper } from "./lib/i18n/locale-config-wrapper";
import { LocaleProvider } from "./lib/i18n/provider";
import { routeTree } from "./routeTree.gen";
import { initializeSentry } from "./sentry-config";
import { AnalyticsProvider } from "./shared/components/analytics-provider";
import { GlobalErrorBoundary } from "./shared/components/error-boundary";
import { GlobalErrorListener } from "./shared/components/error-simulation-components";
import { NotFoundPage } from "./shared/components/not-found-page";
import { queryClient } from "./shared/lib/query-client";
import { getColorSchemeOverride } from "./shared/utils/embed-detection";
import { initializeIframeTelemetry } from "./shared/utils/iframe-telemetry";
import "./styles/global.css";

// Make version available globally
if (typeof window !== "undefined") {
  window.version = packageJson.version;
}

// Also make it available as a global variable (not just on window)
globalThis.version = packageJson.version;

// Polyfill for assert function used by bitcore-lib
if (typeof window !== "undefined") {
  // @ts-expect-error
  window.assert = (condition: unknown, message?: string) => {
    if (!condition) {
      throw new Error(message || "Assertion failed");
    }
  };
}

// Initialize services as early as possible
initializeSentry();
initializeIframeTelemetry();
initializeI18n();

// Create a new router instance
const router = createRouter({
  routeTree,
  defaultPreload: "intent",
  defaultStaleTime: 5000,
  defaultNotFoundComponent: () => <NotFoundPage />,
});

// Register the router instance for type safety
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }

  interface HistoryState {
    backTo: DefiTabRouteId;
  }
}

async function startApp() {
  const rootElement = document.getElementById("root");

  if (!rootElement) {
    throw new Error("Root element not found");
  }

  // Get color scheme override from URL parameters for customer embedding
  // Validation ensures only "light" or "dark" values are returned, preventing XSS
  const colorSchemeOverride = getColorSchemeOverride();

  // Create root and render immediately
  const root = createRoot(rootElement);

  root.render(
    <StrictMode>
      <DesignSystemUi forceColorScheme={colorSchemeOverride || undefined}>
        <LocaleProvider>
          <GlobalErrorBoundary>
            <GlobalErrorListener />
            <Suspense
              fallback={
                <Center style={{ height: "100vh" }}>
                  <Loader size="lg" />
                </Center>
              }
            >
              <QueryClientProvider client={queryClient}>
                <WidgetConfigProvider>
                  <LocaleConfigWrapper>
                    <WalletProvider>
                      <AnalyticsProvider>
                        <RouterProvider router={router} />
                      </AnalyticsProvider>
                    </WalletProvider>
                  </LocaleConfigWrapper>
                </WidgetConfigProvider>
              </QueryClientProvider>
            </Suspense>
          </GlobalErrorBoundary>
        </LocaleProvider>
      </DesignSystemUi>
    </StrictMode>,
  );
}

startApp();
